import classNames from 'classnames';
import DurationFormat from 'components/DurationFormat';
import HourStateLabel from 'components/HourStateLabel';
import { Info } from 'components/Icons';
import { MultiCurrencyWrapper } from 'components/MultiCurrencyWrapper';
import BudgetIsPendingWrap from 'components/projects/BudgetIsPendingWrapper';
import Tooltip from 'components/Tooltip';
import type { TrProps } from 'components/ui-blocks/table';
import { Td, Tr } from 'components/ui-blocks/table';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { Column } from 'constants/filters';
import {
  COLUMN_BILLABLE_HOURS,
  COLUMN_BILLED_HOURS,
  COLUMN_BILLED_MONEY,
  COLUMN_BUDGET,
  COLUMN_BUDGET_REMAINING,
  COLUMN_CAPACITY,
  COLUMN_DAY,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_INTERNAL_COST,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_LOGGED_PERCENTAGE,
  COLUMN_NAME,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_OVERTIME,
  COLUMN_PROFIT,
  COLUMN_PROFITABILITY,
  COLUMN_PROJECT,
  COLUMN_STATE,
  COLUMN_UNBILLED_HOURS,
  COLUMN_UNBILLED_MONEY,
  COLUMN_UNDERTIME,
} from 'constants/filters';
import { without } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import type { GenericWidgetRow } from 'redux/modules/superreports.types';
import type { Currency } from 'types/account';
import { notNull } from 'utils/typesafe';
import { ViewportWidthBreakpoint } from 'utils/viewport';
import { REPORT_DURATION_FORMAT } from '../../constants';
import TableWidgetBarBlock from '../blocks/TableWidgetBarBlock';
import TableWidgetBudgetValueBlock from '../blocks/TableWidgetBudgetValueBlock';
import { TableWidgetPlaceholderBlock } from '../blocks/TableWidgetPlaceholderBlock';
import TableWidgetPrimaryBlock from '../blocks/TableWidgetPrimaryBlock';
import { TableWidgetProjectBlock } from '../blocks/TableWidgetProjectBlock';
import { getDepthStyle, hasHourDetails, replaceZeroMoney } from '../helpers';
import styles from '../TableWidget.module.scss';

export interface TableWidgetBodyRowProps extends TrProps {
  canSelect?: boolean;
  columns: Array<Column>;
  currency?: Currency;
  currentWidthBreakpoint: ViewportWidthBreakpoint;
  data: GenericWidgetRow;
  depth?: number;

  /**
   * Whether the row is currently expanded. `null` means the row cannot be expanded.
   */
  expandedState: boolean | null;

  hasBatchSelect?: boolean;

  hiddenColumns?: Array<Column>;

  isHour?: boolean;

  isLoading?: boolean;

  isSectionHeader?: boolean;

  readOnly?: boolean;

  /**
   * Whether the row is currently selected. `null` means the selection is indeterminate.
   */
  selectedState?: boolean | null;

  onEdit?: () => void;
  onToggleExpansion?: () => void;
  onToggleSelection?: () => void;
}

/**
 * Replaces zero values with a placeholder and displays a tooltip if there are multiple currencies,
 * while ensuring that the tooltip is not displayed if the value is zero.
 */
function moneyWrapper(money: string | null | undefined, hasMultipleCurrencies = false) {
  const value = replaceZeroMoney(money);
  if (React.isValidElement(value) && value.type === TableWidgetPlaceholderBlock) {
    return value;
  }

  return (
    <MultiCurrencyWrapper hasMultipleCurrencies={hasMultipleCurrencies}>
      {value}
    </MultiCurrencyWrapper>
  );
}

const TableWidgetBodyRow: React.FC<TableWidgetBodyRowProps> = ({
  canSelect,
  currency,
  currentWidthBreakpoint,
  data,
  depth,
  columns: originalColumns,
  expandedState,
  hasBatchSelect,
  hiddenColumns = EMPTY_ARRAY,
  isHour,
  isLoading,
  isSectionHeader,
  readOnly,
  selectedState,
  style,
  onEdit,
  onToggleExpansion,
  onToggleSelection,
}) => {
  const handleClick = useCallback(() => {
    if (isHour) {
      onEdit?.();
    } else if (expandedState !== null) {
      onToggleExpansion?.();
    }
  }, [expandedState, isHour, onEdit, onToggleExpansion]);

  const columns = useMemo(
    () => (hiddenColumns.length ? without(originalColumns, ...hiddenColumns) : originalColumns),
    [hiddenColumns, originalColumns]
  );

  const cells: Array<JSX.Element> = columns
    .map((column, index) => {
      switch (column) {
        case COLUMN_NAME:
        case COLUMN_DAY:
          return (
            <Td data-testid="tablewidgetbodyrow-name" key={column} innerClass={styles.primary}>
              <TableWidgetPrimaryBlock
                key={column}
                data={data}
                expandedState={isHour ? null : expandedState}
                canSelect={(isHour ? !readOnly : canSelect) && !!onToggleSelection}
                selectedState={selectedState}
                onToggleSelection={() => !readOnly && onToggleSelection?.()}
                isLoading={isLoading}
                hideCheckbox={isHour && (readOnly || canSelect === false)}
                isHour={isHour}
                hourColumns={columns}
                hasBatchSelect={hasBatchSelect}
              />
            </Td>
          );
        case COLUMN_LOGGED_HOURS:
          return (
            <Td data-testid="tablewidgetbodyrow-loggedhours" key={column} numeric>
              <DurationFormat
                value={data.loggedMinutes}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );
        case COLUMN_LOGGED_PERCENTAGE:
          return (
            <Td data-testid="tablewidgetbodyrow-loggedhours-pc" key={column} numeric>
              {data.loggedPercentage}%
            </Td>
          );
        case COLUMN_LOGGED_MONEY:
          return (
            <Td
              data-testid="tablewidgetbodyrow-loggedmoney"
              key={column}
              numeric
              className={classNames({ [styles.lineThrough]: !!data.markedAsUnbillable })}
            >
              {moneyWrapper(data.loggedMoney, data.hasMultipleCurrencies)}
            </Td>
          );

        case COLUMN_BILLED_HOURS:
          return (
            <Td data-testid="tablewidgetbodyrow-billedhours" key={column} numeric>
              <DurationFormat
                value={data.billedMinutes}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_BILLED_MONEY:
          return (
            <Td data-testid="tablewidgetbodyrow-billedmoney" key={column} numeric>
              {moneyWrapper(data.billedMoney, data.hasMultipleCurrencies)}
            </Td>
          );

        case COLUMN_UNBILLED_HOURS:
          return (
            <Td data-testid="tablewidgetbodyrow-unbilledhours" key={column} numeric>
              <DurationFormat
                value={data.unbilledMinutes}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_UNBILLED_MONEY:
          return (
            <Td data-testid="tablewidgetbodyrow-unbilledmoney" key={column} numeric>
              {moneyWrapper(data.unbilledMoney, data.hasMultipleCurrencies)}
            </Td>
          );

        case COLUMN_INTERNAL_COST:
          return (
            <Td data-testid="tablewidgetbodyrow-loggedcost" key={column} numeric>
              {moneyWrapper(data.internalCost, data.hasMultipleCurrencies)}
            </Td>
          );
        case COLUMN_PROFIT:
          return (
            <Td data-testid="tablewidgetbodyrow-profit" key={column} numeric>
              {moneyWrapper(data.profit, data.hasMultipleCurrencies)}
            </Td>
          );
        case COLUMN_PROFITABILITY:
          return (
            <Td data-testid="tablewidgetbodyrow-profitability" key={column} numeric>
              {typeof data.profitability === 'number' && data.profitability > 0 ? (
                `${data.profitability.toFixed(2)}%`
              ) : (
                <TableWidgetPlaceholderBlock />
              )}
            </Td>
          );
        case COLUMN_ESTIMATED_HOURS:
          return (
            <Td data-testid="tablewidgetbodyrow-plannedhours" key={column} numeric>
              <DurationFormat
                value={data.plannedMinutes}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_ESTIMATED_MONEY:
          return (
            <Td
              data-testid="tablewidgetbodyrow-plannedmoney"
              key={column}
              numeric
              className={classNames({ [styles.lineThrough]: !!data.markedAsUnbillable })}
            >
              {moneyWrapper(data.plannedMoney, data.hasMultipleCurrencies)}
            </Td>
          );

        case COLUMN_BILLABLE_HOURS:
          return (
            <Td data-testid="tablewidgetbodyrow-billablehours" key={column} numeric>
              <DurationFormat
                value={data.billableHours}
                valueType="hours"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_NON_BILLABLE_HOURS:
          return (
            <Td data-testid="tablewidgetbodyrow-nonbillablehours" key={column} numeric>
              <DurationFormat
                value={data.nonBillableHours}
                valueType="hours"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_BUDGET:
          if (isHour || isSectionHeader) {
            return (
              <Td data-testid="tablewidgetbodyrow-budget" key={column} numeric colSpan={2}>
                <TableWidgetPlaceholderBlock />
              </Td>
            );
          }

          if (data.budgetValue == null) {
            return (
              <Td
                data-testid="tablewidgetbodyrow-recbudget"
                key={column}
                rightAligned
                colSpan={2}
                className={styles.info}
              >
                <Tooltip title="Recurring budgets cannot be displayed in reports">
                  <Info style={{ width: 16, height: 16, position: 'relative', top: 2 }} />
                </Tooltip>
              </Td>
            );
          }

          return (
            <TableWidgetBarBlock
              key={column}
              currency={currency}
              currentWidthBreakpoint={currentWidthBreakpoint}
              progress={data.budgetPercent}
              type={data.budgetType}
              value={data.budgetValue}
              pending={data.budgetIsPending}
              columnKey={index}
            />
          );

        case COLUMN_BUDGET_REMAINING: {
          const value =
            data.budgetValue != null && data.budgetProgress != null
              ? data.budgetValue - data.budgetProgress
              : undefined;
          return (
            <Td data-testid="tablewidgetbodyrow-rembudget" key={column} numeric>
              <BudgetIsPendingWrap
                tooltipPosition="left"
                title="Budget is being calculated..."
                isPending={!!data.budgetIsPending}
              >
                <TableWidgetBudgetValueBlock
                  type={data.budgetType}
                  value={value}
                  currency={currency}
                />
              </BudgetIsPendingWrap>
            </Td>
          );
        }

        case COLUMN_PROJECT:
          return (
            <Td data-testid="tablewidgetbodyrow-project" key={column}>
              <TableWidgetProjectBlock project={data.project} />
            </Td>
          );

        case COLUMN_CAPACITY:
          return (
            <Td key={column} numeric>
              <DurationFormat
                value={data.capacity}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_OVERTIME:
          return (
            <Td key={column} numeric>
              <DurationFormat
                value={data.overtime}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_UNDERTIME:
          return (
            <Td key={column} numeric>
              <DurationFormat
                value={data.undertime}
                valueType="minutes"
                format={REPORT_DURATION_FORMAT}
                zeroPlaceholder={<TableWidgetPlaceholderBlock />}
                trunc={false}
              />
            </Td>
          );

        case COLUMN_STATE: {
          return (
            <Td key={column}>
              {data.state ? (
                <HourStateLabel state={data.state} size="small" inline />
              ) : (
                <TableWidgetPlaceholderBlock />
              )}
            </Td>
          );
        }

        default:
          return null;
      }
    })
    .filter(notNull);

  const interactive = expandedState !== null;

  if (cells.length > 0 && currentWidthBreakpoint > ViewportWidthBreakpoint.Narrow) {
    cells[0] = React.cloneElement(cells[0], {
      style: getDepthStyle(depth, interactive),
    });
  }

  const { hasAny } = hasHourDetails(data, originalColumns);

  return (
    <Tr
      style={style}
      className={classNames({
        [styles.expandable]: expandedState !== null,
        [styles.expanded]: expandedState === true,
        [styles.withDetails]: hasAny,
        [styles.hourRow]: isHour,
        [styles.readOnly]: isHour && readOnly,
        [styles.sectionHeader]: isSectionHeader,
        [styles.billed]: isHour && (data.isBilled || data.isInvoiced || data.isLocked),
        [styles.batchSelected]: !!selectedState,
      })}
      onClick={handleClick}
      interactive={interactive}
      selected={!!expandedState}
    >
      {cells}
    </Tr>
  );
};

export default TableWidgetBodyRow;
